<template>
  <div class="border-t border-slate-200 bg-white flex-shrink-0">
    <!-- 引用回复预览 -->
    <div v-if="chatStore.replyToMessage" class="px-4 pt-2 flex items-center gap-2">
      <div class="flex-1 flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border-l-2 border-l-primary-400">
        <div class="flex-1 min-w-0">
          <p class="text-[11px] text-primary-600 font-medium">{{ chatStore.replyToMessage.senderName }}</p>
          <p class="text-xs text-slate-500 truncate">{{ chatStore.replyToMessage.content || chatStore.replyToMessage.fileName || '[文件]' }}</p>
        </div>
        <button
          class="p-0.5 text-slate-400 hover:text-slate-600 rounded transition-colors cursor-pointer flex-shrink-0"
          @click="chatStore.clearReplyTo()"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- 工具栏 -->
    <div class="flex items-center gap-1 px-4 pt-2">
      <!-- 表情按钮 -->
      <div class="relative" ref="emojiRef">
        <button
          class="p-1.5 rounded-lg transition-colors cursor-pointer"
          :class="showEmojiPicker ? 'text-primary-500 bg-primary-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'"
          title="表情"
          @click="showEmojiPicker = !showEmojiPicker"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </button>
        <!-- 表情选择器浮窗 -->
        <div v-if="showEmojiPicker" class="absolute bottom-10 left-0 z-50">
          <EmojiPicker @select="handleEmojiSelect" />
        </div>
      </div>

      <!-- 文件按钮 -->
      <button
        class="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
        title="发送文件"
        @click="fileInput?.click()"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
        </svg>
      </button>
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
        @change="handleFileSelect"
      />

      <!-- 图片按钮 -->
      <button
        class="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
        title="发送图片"
        @click="imageInput?.click()"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
      </button>
      <input
        ref="imageInput"
        type="file"
        accept="image/*"
        class="hidden"
        @change="handleImageSelect"
      />
    </div>

    <!-- 输入区域 -->
    <div class="p-3 pt-1">
      <div class="flex items-end gap-2">
        <textarea
          ref="inputRef"
          v-model="inputText"
          placeholder="输入消息... (Enter发送, Shift+Enter换行)"
          rows="1"
          class="flex-1 resize-none text-sm text-slate-700 bg-transparent border-none outline-none leading-relaxed max-h-32 placeholder:text-slate-400"
          @keydown.enter.exact.prevent="handleSend"
          @keydown.enter.shift.exact="handleNewLine"
          @input="autoResize"
          @focus="showEmojiPicker = false"
        ></textarea>
        <button
          class="p-2 rounded-lg transition-colors flex-shrink-0 cursor-pointer"
          :class="inputText.trim()
            ? 'bg-primary-500 text-white hover:bg-primary-600'
            : 'bg-slate-100 text-slate-400 cursor-not-allowed'"
          :disabled="!inputText.trim()"
          @click="handleSend"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { useChatStore } from '@/stores/chat'
import EmojiPicker from './EmojiPicker.vue'

const emit = defineEmits(['send', 'sendFile', 'sendImage'])
const chatStore = useChatStore()

const inputText = ref('')
const inputRef = ref(null)
const fileInput = ref(null)
const imageInput = ref(null)
const showEmojiPicker = ref(false)
const emojiRef = ref(null)

function handleSend() {
  if (!inputText.value.trim()) return
  emit('send', inputText.value)
  inputText.value = ''
  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.style.height = 'auto'
    }
  })
}

function handleNewLine() {
  // Shift+Enter 换行 - 默认行为
}

function autoResize() {
  const el = inputRef.value
  if (el) {
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 128) + 'px'
  }
}

function handleEmojiSelect(emoji) {
  inputText.value += emoji
  showEmojiPicker.value = false
  inputRef.value?.focus()
}

function handleFileSelect(event) {
  const file = event.target.files?.[0]
  if (!file) return

  const fileSizeStr = file.size < 1024 * 1024
    ? (file.size / 1024).toFixed(0) + 'KB'
    : (file.size / (1024 * 1024)).toFixed(1) + 'MB'

  emit('sendFile', {
    fileName: file.name,
    fileSize: fileSizeStr,
    file
  })

  // 重置 input 允许重复选择同文件
  event.target.value = ''
}

function handleImageSelect(event) {
  const file = event.target.files?.[0]
  if (!file) return

  const reader = new FileReader()
  reader.onload = (e) => {
    emit('sendImage', {
      imageUrl: e.target.result,
      fileName: file.name,
      file
    })
  }
  reader.readAsDataURL(file)

  event.target.value = ''
}

// 点击外部关闭 emoji
function handleClickOutside(e) {
  if (emojiRef.value && !emojiRef.value.contains(e.target)) {
    showEmojiPicker.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// 外部聚焦方法
function focus() {
  inputRef.value?.focus()
}

defineExpose({ focus })
</script>
